import React from 'react'
import Web3 from 'web3'
import { AbiItem } from 'web3-utils'
import { Contract } from 'web3-eth-contract'
import { fromWei, toWei } from 'web3-utils'
import { useAppState, useDispatch, setWeb3, setVPool } from 'state'
import vTokenABI from 'abi/vToken'
import { AAVE_KOVAN, AAVE_VPOOL, LoanInfo } from 'models'
import { useTokenBalance } from 'hooks'
import { daysFromNow } from 'toolbelt'
import BN from 'bn.js'
import { toast } from 'react-toastify'

export function usePaladin() {
  const dispatch = useDispatch()
  const { approve } = useTokenBalance()
  const { provider, account, borrowRate, vPool } = useAppState()
  const [web3, setWeb3] = React.useState<Web3 | undefined>(undefined)

  React.useEffect(() => {
    if (provider && !web3 && !vPool) {
      const _web3 = new Web3(provider)
      setWeb3(_web3)
      const _vpool = new _web3.eth.Contract(vTokenABI as AbiItem[], AAVE_VPOOL)
      dispatch(setVPool(_vpool))
    }
  }, [provider, web3, vPool])

  const txCallback = (txHash: string) => {
    console.log(`txCallback`)
    // toast(`wow, this is txHash ${txHash}`, {
    //   position: 'top-right',
    //   autoClose: 10000,
    // })
  }

  async function deposit(amount: string) {
    try {
      await approve()
      if (vPool) vPool.methods.deposit(toWei(amount).toString()).send({ from: account }, txCallback)
    } catch (err) {
      console.log(`error depositing ${amount}: ${JSON.stringify(err)}`)
    }
  }

  async function withdraw(amount: string) {
    try {
      if (vPool) vPool.methods.withdraw(toWei(amount).toString()).send({ from: account }, txCallback)
    } catch (err) {
      console.log(`error withdrawing ${amount}: ${JSON.stringify(err)}`)
    }
  }

  async function borrow(amount: string, fee: string) {
    try {
      await approve()
      console.log(`[borrow] approved`)
      if (vPool) vPool.methods.borrow(toWei(amount), fee).send({ from: account }, txCallback)
    } catch (err) {
      console.log(`error borrowing ${amount} with fee ${fee}: ${err}`)
    }
  }

  function calculateFee(votingPower: string, terminationDate: string) {
    if (!votingPower || !terminationDate) return ''
    try {
      if (vPool) {
        const blocksFromNow = daysFromNow(terminationDate) * 4 * 60 * 24
        let res = new BN(borrowRate).mul(new BN(toWei(votingPower)))
        res = res.muln(blocksFromNow)
        res = res.div(new BN(10).pow(new BN(18)))
        return res.toString()
      }
    } catch (err) {
      console.log(`error getting borrow rate: ${err}`)
      return ''
    }
  }

  // calculate loan termination date from fee
  // returns days from now
  function calculateTerminationDate(votingPower: string, fee: string) {
    if (!votingPower || !fee) return undefined
    try {
      const normalizedFee = new BN(fee).mul(new BN(10).pow(new BN(18)))
      const blocksFromNow = normalizedFee.div(new BN(borrowRate).mul(new BN(votingPower)))
      const daysFromNow = blocksFromNow.divn(4 * 60 * 24)
      return daysFromNow.toNumber()
    } catch (err) {
      console.log(`error calculating termination date ${err}`)
    }
  }

  async function getLoans() {
    try {
      if (vPool) {
        const loans: string[] = await vPool.methods.getLoansByBorrowerStored(account).call({ from: account })
        console.log(JSON.stringify(loans))
        const res = await Promise.all(
          loans.map((loan) => vPool.methods.getBorrowDataStored(loan).call({ from: account }))
        )
        return res as LoanInfo[]
      } else {
        console.log(`oops no vpool`)
      }
    } catch (err) {
      console.log(`error getting loans by ${account}: ${JSON.stringify(err)}`)
    }
    return []
  }

  async function closeLoan(loanPool: string, borrower: string) {
    console.log(`closing loan ${loanPool} borrower ${borrower}`)
    try {
      if (vPool) vPool.methods.closeBorrow(loanPool).send({ from: account }, txCallback)
    } catch (err) {
      console.log(`error closing borrow ${loanPool}: ${JSON.stringify(err)}`)
    }
  }

  async function extendLoan(loanPool: string, fee: string) {
    try {
      // if fee was paid, the token is already approved
      if (vPool) vPool.methods.expandBorrow(loanPool, fee).send({ from: account }, txCallback)
    } catch (err) {
      console.log(`error expanding borrow ${loanPool}: ${JSON.stringify(err)}`)
    }
  }

  async function getAPY() {
    try {
      if (vPool) {
        const supplyRate = await vPool.methods.supplyRatePerBlock().call({ from: account })
        console.log(`supply rate: ${supplyRate}`)
        return new BN(supplyRate).muln(4 * 60 * 24 * 365).toString()
      }
    } catch (err) {
      console.log(`error getting supply rate: ${err}`)
    }
  }

  return { deposit, withdraw, borrow, calculateFee, getLoans, closeLoan, extendLoan, calculateTerminationDate, getAPY }
}
